import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';

import { Button } from '../PageHeader/Button';
import './AdvancedFilters.scss';
import Strings from '../../Strings';
import { actionsAdvancedFilters } from './actions';
import SideList from './internals/SideList';
import Checkbox from './internals/Checkbox';
import Input from './internals/Input';
import DatePicker from './internals/DatePicker';
import getFilteredDefinitionValues from './hierarchy';

const VALUES_COUNT_TO_SHOW_FILTER_IN_LIST = 15;

function Filter(props) {
  const { definition, filters, updateFilterState, onClose, notifWhoOpened, lastOpenedChild, disabled } = props;
  const [searchTerm, setSearchTerm] = useState('');

  const filteredValues = getFilteredDefinitionValues(definition, searchTerm);

  return (
    <SideList
      title={definition.text}
      tooltip={definition.tooltip}
      notifWhoOpened={notifWhoOpened}
      lastOpenedChild={lastOpenedChild}
      filters={filters}
      updateFilterState={updateFilterState}
      disabled={disabled}
    >
      {definition.type === 'number' ? (
        <Input filterDefinition={definition} filters={filters} updateFilterState={updateFilterState} />
      ) : definition.type === 'date' ? (
        <DatePicker filterDefinition={definition} filters={filters} updateFilterState={updateFilterState} onClose={onClose} />
      ) : (
        <>
          {definition.values.length === 0 && <div className="no-options">{Strings.noOptions}</div>}
          {definition.values.length > VALUES_COUNT_TO_SHOW_FILTER_IN_LIST && (
            <input
              type="text"
              placeholder={Strings.search}
              className="advanced-filters-search"
              onChange={e => setSearchTerm(e.target.value)}
            />
          )}
          {filteredValues.map((e, i) => {
            const checkboxes = [];
            
            if (e.hierarchyBranchValues) {
              checkboxes.push(
                <Checkbox
                  text={`${Strings.all} ${e.text}`}
                  group={definition.group}
                  value={`hierarchy_branch_${e.value}`}
                  filters={filters}
                  updateFilterState={updateFilterState}
                  definition={definition}
                  key={`hierarchy_branch_${e.value}`}
                  isGroupToggle
                  toggleValues={e.hierarchyBranchValues}
                  hierarchyDepth={e.hierarchyBranchDepth}
                />
              );
            // Add the legacy group toggle used by non-hierarchical filters.
            } else if (e.groupedOptionName && (i === 0 || filteredValues[i - 1].groupedOptionName !== e.groupedOptionName)) {
              checkboxes.push(
                <Checkbox 
                  text={`All ${e.groupedOptionName}`} 
                  group={definition.group} 
                  value={`group_${e.groupedOptionName}`} 
                  filters={filters}
                  updateFilterState={updateFilterState}
                  definition={definition}
                  key={`group_${e.groupedOptionName}`}
                  isGroupToggle
                  groupName={e.groupedOptionName}
                />
              );
            }
            
            // Add regular checkbox
            checkboxes.push(
              <Checkbox 
                text={e.text} 
                group={definition.group} 
                value={e.value} 
                filters={filters}
                updateFilterState={updateFilterState}
                definition={definition}
                key={e.value}
                hierarchyDepth={e.hierarchyDepth}
              >
                {definition.itemRender ? definition.itemRender(e) : null}
              </Checkbox>
            );
            return checkboxes;
          }).flat()}
        </>
      )}
    </SideList>
  );
}

Filter.propTypes = {
  definition: PropTypes.object.isRequired,
  filters: PropTypes.array,
  updateFilterState: PropTypes.func,
  onClose: PropTypes.func,
  notifWhoOpened: PropTypes.func,
  lastOpenedChild: PropTypes.string,
  disabled: PropTypes.any,
};

function AdvancedFilters(props) {
  const { children, isFiltersInitialized, initializeFilterState, filters, updateFilterState, expandToRight } = props;
  const [show, setShow] = useState(false);
  const [lastOpenedChild, setLastOpenedChild] = useState('');

  const childrenArray = React.useMemo(
    () => Array.isArray(children) ? children.filter(c => c) : ([children] || []).filter(c => c),
    [children]
  );


  const syncFiltersWithChildren = () => {
    if (childrenArray) {
      const urlParams = new URLSearchParams(window.location.search);
      const filtersSerialized = urlParams.get('filters');
      const filtersFromUrl = JSON.parse(filtersSerialized);

      childrenArray
        .forEach(child => {
          const group = child.props.definition.group;
          let values;
          const f = filtersFromUrl?.find(e => e.split(':')[0] === group);
          if (f) {
            values = f.split(':')[1].split(',');
          }
          if (child.props.definition.type === 'number') {
            const filterExists = filters.some(filter => filter.group === group && filter.value === group);
            if (!filterExists && values?.length > 0) {
              updateFilterState({
                group,
                value: group,
                state: parseInt(values[0], 10) || '',
              });
            }
          } else if (child.props.definition.type === 'date') {
            const filterExists = filters.some(filter => filter.group === group && filter.value === group);
            if (!filterExists && values?.length > 0) {
              const dateValue = values?.length > 0 ? moment(values[0]) : null;
              updateFilterState({
                group,
                value: group,
                state: dateValue?.isValid() ? dateValue : null,
              });
            }
          } else {
            child.props.definition.values.forEach(e => {
              const filterExists = filters.some(filter => filter.group === group && filter.value === e.value);
              if (!filterExists && values?.includes(e.value)) {
                updateFilterState({
                  group,
                  value: e.value,
                  state: true,
                });
              }
            });
          }
        });
    }
  };

  useEffect(() => {
    syncFiltersWithChildren();
    if (!isFiltersInitialized) {
      initializeFilterState();
    }
  }, [childrenArray, filters, isFiltersInitialized]);

  const blurHandler = event => {
    // if Click is outside of this list components, lets close it, mean that we lost focus
    if (!event.currentTarget.contains(event.relatedTarget)) {
      // Defer the check to allow MUI date picker portals (rendered outside the DOM tree)
      // to receive focus before we decide to close the panel
      const currentTarget = event.currentTarget;
      setTimeout(() => {
        const active = document.activeElement;
        const isMuiPortal = active?.closest('.MuiPickersPopper-root, .MuiDateCalendar-root, [class*="MuiPicker"]');
        if (!currentTarget.contains(active) && !isMuiPortal) {
          setShow(false);
        }
      }, 0);
    }
  };


  // callback for children to inform about changes of their open state
  const notifWhoOpened = e => {
    setLastOpenedChild(e);
  };

  const getChildren = () => {
    return childrenArray
      .map((child, i) => {
        return React.cloneElement(child, {
          notifWhoOpened: e => notifWhoOpened(e),
          lastOpenedChild,
          key: i,
          filters,
          updateFilterState,
          onClose: () => setShow(false),
        });
    });
  };
  
  const dropdownList = (
    <div key="dropdownList" className="dropdown-list">
      {getChildren() || null}
    </div>
  );

  const isDisabled = !children;

  
  const button = (
    <Button
      class="addfilter"
      title={Strings.addFilter}
      onClick={() => {
        setShow(!show);
      }}
      disabled={isDisabled}
      testid="add-filter"
    />
  );


  return (
    <div
      className={`advanced-filters${expandToRight ? ' right-expanding' : ''}`}
      tabIndex={1}
      onBlur={e => blurHandler(e)}
    >
      {button}
      {show && dropdownList}
    </div>
  );
}

AdvancedFilters.propTypes = {
  children: PropTypes.any,
  filters: PropTypes.array,
  isFiltersInitialized: PropTypes.bool,
  updateFilterState: PropTypes.func,
  initializeFilterState: PropTypes.func,
  expandToRight: PropTypes.bool,
};

const mapDispatchToProps = dispatch => ({
  updateFilterState: state => dispatch(actionsAdvancedFilters.updateFilterState(state)),
  initializeFilterState: () => dispatch(actionsAdvancedFilters.initializeFilterState()),
});

const mapStateToProps = state => {
  return {
    filters: state.entities.advancedFilters.filters.items,
    isFiltersInitialized: state.entities.advancedFilters.filters.isInitalized,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(AdvancedFilters);
export { Filter };
